import { DepositType, PrepaymentMode, normalizeDepositType, normalizePrepaymentMode } from '../constants/PrepaymentMode';

const roundToCents = (value) => Math.round(value * 100) / 100;

/** The deposit a service takes at a given price, capped at the price. Mirrors the API. */
export const resolveDeposit = (price, depositType, depositValue) => {
  const parsedPrice = Number(price);
  const parsedValue = Number(depositValue);
  if (!Number.isFinite(parsedPrice) || parsedPrice <= 0 || !Number.isFinite(parsedValue) || parsedValue <= 0) {
    return 0;
  }

  const deposit = normalizeDepositType(depositType) === DepositType.Percentage
    ? roundToCents((parsedPrice * parsedValue) / 100)
    : parsedValue;

  return Math.min(parsedPrice, deposit);
};

/** What the customer pays now and at the store for a single service, for the editor preview. */
export const calculateServicePrepaymentPreview = ({ price, prepaymentMode, depositType, depositValue }) => {
  const parsedPrice = Number(price);
  if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
    return null;
  }

  const mode = normalizePrepaymentMode(prepaymentMode);
  const deposit = resolveDeposit(parsedPrice, depositType, depositValue);
  if (mode === PrepaymentMode.FullPrepayment) {
    return { payNow: parsedPrice, payAtStore: 0, nonRefundable: deposit };
  }

  if (mode === PrepaymentMode.Deposit) {
    return { payNow: deposit, payAtStore: roundToCents(parsedPrice - deposit), nonRefundable: deposit };
  }

  return { payNow: 0, payAtStore: parsedPrice, nonRefundable: 0 };
};

/**
 * Structural checks only; whether the deposit covers the processing fee depends on the store's
 * payment methods, so the API has the final word on that.
 * @returns {string|null} i18n key of the first problem found
 */
export const validateServicePrepayment = ({ price, prepaymentMode, depositType, depositValue }) => {
  const mode = normalizePrepaymentMode(prepaymentMode);
  if (mode === PrepaymentMode.NotRequired) {
    return null;
  }

  const hasValue = depositValue !== '' && depositValue !== null && depositValue !== undefined;
  const parsedValue = Number(depositValue);

  if (mode === PrepaymentMode.Deposit && (!hasValue || !Number.isFinite(parsedValue) || parsedValue <= 0)) {
    return 'servicesManagement.validation.depositRequired';
  }

  if (!hasValue || parsedValue <= 0) {
    return null;
  }

  if (normalizeDepositType(depositType) === DepositType.Percentage && parsedValue > 100) {
    return 'servicesManagement.validation.depositPercentageRange';
  }

  if (normalizeDepositType(depositType) === DepositType.FixedAmount && parsedValue > Number(price)) {
    return 'servicesManagement.validation.depositExceedsPrice';
  }

  return null;
};
